/**
 * Luma Desktop Bridge
 *
 * Mirrors the pattern of widgetPlugin.ts but for Electron IPC.
 * All calls are no-ops when not running inside Electron, so the same
 * codebase runs on iOS and web without conditionals everywhere.
 *
 * Usage in usePomodoroTimer.tsx (alongside existing syncWidgetWithTimer):
 *   syncDesktopWithTimer({ timerState, activeTask, timerMode, timeRemaining, elapsedTime, sessionDuration })
 */

import type { DesktopTimerState, DesktopPrefs } from '@/types/electron';

// ─────────────────────────────────────────────────────────────────────────────
// Guards
// ─────────────────────────────────────────────────────────────────────────────

/** True when running inside an Electron renderer */
export function isDesktopApp(): boolean {
  return typeof window !== 'undefined' && typeof window.electronAPI !== 'undefined';
}

/**
 * Returns the widget window type passed as ?__win= query param.
 * 'menubar' | 'floating' | null (null = main window)
 */
export function getWindowType(): 'menubar' | 'floating' | null {
  if (typeof window === 'undefined') return null;
  const val = new URLSearchParams(window.location.search).get('__win');
  if (val === 'menubar' || val === 'floating') return val;
  return null;
}

/** True when this renderer is the main Luma app window (not a widget) */
export function isMainDesktopWindow(): boolean {
  return isDesktopApp() && getWindowType() === null;
}

/** True only in a Mac App Store build, where Apple IAP (not Stripe) must be used. */
export function isMacAppStore(): boolean {
  return isDesktopApp() && window.electronAPI!.mas === true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Outbound — main window → Electron main process → widgets
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Syncs the current timer state to Electron main process, which forwards
 * it to the menubar popover and floating pill windows.
 *
 * Only executes in the main window — no-op in widget windows or web/iOS.
 */
export function syncDesktopWithTimer(params: {
  timerState: string;
  activeTask: { content: string } | null;
  timerMode: 'maker' | 'manager' | null;
  timeRemaining: number;
  elapsedTime: number;
  sessionDuration: number;
}): void {
  if (!isMainDesktopWindow()) return;

  const state: DesktopTimerState = {
    timerState: params.timerState,
    timeRemaining: params.timeRemaining,
    elapsedTime: params.elapsedTime,
    sessionDuration: params.sessionDuration,
    activeTaskName: params.activeTask?.content ?? null,
    timerMode: params.timerMode,
  };

  window.electronAPI!.sendTimerUpdate(state);
}

// ─────────────────────────────────────────────────────────────────────────────
// Inbound — Electron main process → main window (control commands from widgets)
// ─────────────────────────────────────────────────────────────────────────────

type ControlCallbacks = {
  onPause:  () => void;
  onResume: () => void;
  onStop:   () => void;
};

/**
 * Registers listeners for timer control commands dispatched from widget windows.
 * Call once in the main window (inside useDesktopSync hook).
 * Returns a cleanup function.
 */
export function listenForDesktopControls(callbacks: ControlCallbacks): () => void {
  if (!isMainDesktopWindow()) return () => undefined;

  const api = window.electronAPI!;
  const cleanupPause  = api.onPauseTimer(callbacks.onPause);
  const cleanupResume = api.onResumeTimer(callbacks.onResume);
  const cleanupStop   = api.onStopTimer(callbacks.onStop);

  return () => {
    cleanupPause();
    cleanupResume();
    cleanupStop();
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Desktop widget preferences (persisted in localStorage, mirrored to main process)
// ─────────────────────────────────────────────────────────────────────────────

const PREFS_KEY = 'luma.desktopPrefs';

export const DEFAULT_DESKTOP_PREFS: DesktopPrefs = {
  floatingEnabled: true,
  floatingPinned: false,
  menuBarCountdown: true,
};

export function getDesktopPrefs(): DesktopPrefs {
  if (typeof window === 'undefined') return DEFAULT_DESKTOP_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_DESKTOP_PREFS;
    return { ...DEFAULT_DESKTOP_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_DESKTOP_PREFS;
  }
}

/** Persist prefs and push them to the Electron main process. */
export function setDesktopPrefs(prefs: DesktopPrefs): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable — ignore */
    }
  }
  if (isDesktopApp()) window.electronAPI!.setDesktopPrefs(prefs);
}

/** Send the persisted prefs to the main process. Call once when the main window mounts. */
export function syncDesktopPrefs(): void {
  if (!isMainDesktopWindow()) return;
  window.electronAPI!.setDesktopPrefs(getDesktopPrefs());
}

// ─────────────────────────────────────────────────────────────────────────────
// Payments — open Stripe in the system browser on desktop, navigate on web
// ─────────────────────────────────────────────────────────────────────────────

export function openPaymentUrl(url: string): void {
  if (isDesktopApp()) {
    window.electronAPI!.openExternal(url);
    return;
  }
  if (typeof window !== 'undefined') window.location.href = url;
}
